// Split shipping window (Stephanie 2026-10-16: "add a way to split ship
// orders"). One item → several addresses, with how many of each size go to
// each. The supplier gets ONE PO listing every address and its quantities;
// setup is charged once and the PO's money doesn't change.
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Plus, Trash2, X } from "lucide-react";
import { api, apiErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import {
  draftProblems,
  remainingBySize,
  remainingTotal,
  toDraft,
  type SavedSplit,
  type SplitDraftRow,
  type SplitVariant,
} from "@/lib/ship-split";

interface AddressRow {
  id: string;
  label: string;
  street1: string;
  city: string;
  stateCode: string;
}

const variantLabel = (v: SplitVariant) => [v.color, v.size].filter(Boolean).join(" ") || "Qty";

export function ShipSplitModal(props: {
  orderId: string;
  clientId: string;
  lineItemId: string;
  itemLabel: string;
  quantityTotal: number;
  variants: SplitVariant[];
  saved: SavedSplit[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const { orderId, clientId, lineItemId, itemLabel, quantityTotal, variants, saved, onClose, onSaved } = props;
  const blank = (): SplitDraftRow => ({ addressId: "", qty: {}, total: 0 });
  const [rows, setRows] = useState<SplitDraftRow[]>(() => (saved.length ? toDraft(saved) : [blank(), blank()]));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const addrQ = useQuery({
    queryKey: ["clients", clientId, "addresses"],
    queryFn: () => api<{ addresses: AddressRow[] }>(`/clients/${clientId}/addresses`),
  });
  const addresses = addrQ.data?.addresses ?? [];
  const left = useMemo(() => remainingBySize(variants, rows), [variants, rows]);
  const leftTotal = remainingTotal(quantityTotal, variants, rows);
  const problems = draftProblems(quantityTotal, variants, rows);

  const setRow = (i: number, patch: Partial<SplitDraftRow>) => setRows((rs) => rs.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const num = (s: string) => Math.max(0, Math.floor(Number(s) || 0));

  const save = async (splits: SplitDraftRow[]) => {
    setSaving(true);
    setError(null);
    try {
      await api(`/orders/${orderId}/lines/${lineItemId}/ship-splits`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          splits: splits.map((r) => ({
            address_id: r.addressId,
            quantities: variants.map((v) => ({ variant_id: v.id, qty: r.qty[v.id] ?? 0 })),
            quantity: variants.length ? undefined : r.total,
          })),
        }),
      });
      onSaved();
    } catch (e) {
      setError(apiErrorMessage(e, "Couldn't save the split."));
    } finally {
      setSaving(false);
    }
  };

  const cell = "h-9 w-16 rounded-md border border-rule bg-white px-2 text-right text-sm tabular-nums text-ink";
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-brown-500/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Split shipment"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="flex max-h-[92vh] w-full max-w-4xl flex-col rounded-xl bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between border-b border-brown-50 px-6 py-4">
          <div>
            <h2 className="font-display text-lg font-bold text-brown-500">Split shipment</h2>
            <p className="text-sm text-brown-400">
              {itemLabel} · {quantityTotal} pcs. Pick each address and how many go there. The supplier gets one PO listing
              every address; setup is charged once.
            </p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="rounded p-1 text-brown-300 hover:bg-cream-100">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="overflow-auto px-6 py-4">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="text-[11px] font-medium uppercase tracking-[0.12em] text-muted">
                <th className="py-2 pr-3">Ship to</th>
                {variants.length ? variants.map((v) => <th key={v.id} className="px-1 py-2 text-right">{variantLabel(v)}</th>) : <th className="px-1 py-2 text-right">Qty</th>}
                <th className="px-1 py-2 text-right">Total</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => {
                const rowTotal = variants.length ? variants.reduce((n, v) => n + (r.qty[v.id] ?? 0), 0) : r.total;
                return (
                  <tr key={i} className="border-t border-brown-50">
                    <td className="py-2 pr-3">
                      <select
                        aria-label={`Address ${i + 1}`}
                        value={r.addressId}
                        onChange={(e) => setRow(i, { addressId: e.target.value })}
                        className="h-9 w-full min-w-[220px] rounded-md border border-rule bg-white px-2 text-sm text-ink"
                      >
                        <option value="">{addrQ.isLoading ? "Loading…" : "Pick an address"}</option>
                        {addresses.map((a) => (
                          <option key={a.id} value={a.id}>
                            {a.label} — {a.street1}, {a.city}, {a.stateCode}
                          </option>
                        ))}
                      </select>
                    </td>
                    {variants.length ? (
                      variants.map((v) => (
                        <td key={v.id} className="px-1 py-2 text-right">
                          <input
                            type="number"
                            min={0}
                            inputMode="numeric"
                            aria-label={`${variantLabel(v)} to address ${i + 1}`}
                            value={r.qty[v.id] ?? 0}
                            onChange={(e) => setRow(i, { qty: { ...r.qty, [v.id]: num(e.target.value) } })}
                            className={cell}
                          />
                        </td>
                      ))
                    ) : (
                      <td className="px-1 py-2 text-right">
                        <input type="number" min={0} aria-label={`Qty to address ${i + 1}`} value={r.total} onChange={(e) => setRow(i, { total: num(e.target.value) })} className={cell} />
                      </td>
                    )}
                    <td className="px-1 py-2 text-right font-semibold tabular-nums text-ink">{rowTotal}</td>
                    <td className="py-2 pl-2 text-right">
                      <button
                        type="button"
                        aria-label={`Remove address ${i + 1}`}
                        onClick={() => setRows((rs) => rs.filter((_, j) => j !== i))}
                        className="rounded p-1 text-brown-300 hover:bg-rust-50 hover:text-rust-500"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                );
              })}
              <tr className="border-t border-brown-100 text-[13px]">
                <td className="py-2 pr-3 font-semibold text-brown-400">Still to place</td>
                {variants.length ? (
                  variants.map((v) => (
                    <td key={v.id} className={`px-1 py-2 text-right tabular-nums ${left[v.id] === 0 ? "text-muted" : "font-semibold text-rust-600"}`}>
                      {left[v.id]}
                    </td>
                  ))
                ) : (
                  <td className={`px-1 py-2 text-right tabular-nums ${leftTotal === 0 ? "text-muted" : "font-semibold text-rust-600"}`}>{leftTotal}</td>
                )}
                <td className={`px-1 py-2 text-right font-semibold tabular-nums ${leftTotal === 0 ? "text-ok" : "text-rust-600"}`}>{leftTotal}</td>
                <td />
              </tr>
            </tbody>
          </table>
          <button
            type="button"
            onClick={() => setRows((rs) => [...rs, blank()])}
            className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-ink hover:text-muted"
          >
            <Plus className="h-4 w-4" /> Add address
          </button>
          {addresses.length < 2 && !addrQ.isLoading ? (
            <p className="mt-2 text-[13px] text-brown-400">This client has fewer than two addresses — add them on the account first.</p>
          ) : null}
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-brown-50 px-6 py-4">
          <div className="text-[13px]">
            {error ? <p role="alert" className="font-semibold text-rust-600">{error}</p> : problems.length ? <p className="text-brown-400">{problems[0]}</p> : <p className="font-semibold text-ok">Every piece is placed.</p>}
          </div>
          <div className="flex gap-2">
            {saved.length > 0 && (
              <Button type="button" variant="outline" onClick={() => void save([])} disabled={saving} title="Ship everything to one address again">
                Remove split
              </Button>
            )}
            <Button type="button" variant="outline" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button type="button" onClick={() => void save(rows)} disabled={saving || problems.length > 0}>
              {saving ? "Saving…" : "Save split"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
